import pluralize from 'pluralize'
import { formatShortDateWithYear, parseLocalISODate } from '../../../common/dateUtils'
import { type RunContextItem, type RunContextItemOf, type RunContextKind, type RunContextPayloads } from '../../../store/customWorkflow/customWorkflows'

export type LabelableContextItem = { kind: string } & Partial<RunContextPayloads['aiAgentResult'] & RunContextPayloads['event']>

const humanize = (raw?: string): string => {
  if (!raw) return ''
  const spaced = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
  if (!spaced) return raw
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

const CONTEXT_KIND_LABELS: Record<string, string> = {
  contact: 'Contact',
  ccqExecution: 'Cross-client query',
  meeting: 'Meeting',
  document: 'Document',
  insights: 'Insights',
} satisfies Partial<Record<RunContextKind, string>>

const AGENT_TYPE_LABELS: Record<string, string> = {
  meetingSummary: 'Meeting summary',
  meetingPreparation: 'Meeting preparation',
  meetingAgenda: 'Meeting agenda',
  documentSummary: 'Document summary',
}

export const contextKindLabel = (item: LabelableContextItem): string => {
  if (item.kind === 'aiAgentResult') return AGENT_TYPE_LABELS[item.agentType ?? ''] ?? humanize(item.agentType)
  if (item.kind === 'event') return humanize(item.eventType)
  return CONTEXT_KIND_LABELS[item.kind] ?? humanize(item.kind)
}

export const primaryContextLabel = (context?: RunContextItem[]): string => {
  const item = context?.[0]
  if (!item) return ''
  return item.kind === 'insights' ? insightsScopeLabel(item) : contextKindLabel(item)
}

const dateRangeLabel = (from: string | null, to: string | null): string => {
  const start = parseLocalISODate(from)
  const end = parseLocalISODate(to)
  if (start && end) return `${formatShortDateWithYear(start)} – ${formatShortDateWithYear(end)}`
  if (start) return `From ${formatShortDateWithYear(start)}`
  if (end) return `Until ${formatShortDateWithYear(end)}`
  return ''
}

export const insightsScopeLabel = (item: RunContextItemOf<'insights'>): string => {
  const advisorCount = item.userIds.length
  const parts = [
    contextKindLabel(item),
    dateRangeLabel(item.fromDate, item.toDate),
    advisorCount > 0 ? `${advisorCount} ${pluralize('advisor', advisorCount)}` : '',
  ]
  return parts.filter(Boolean).join(' · ')
}
